import {
  Alert,
  Button,
  Checkbox,
  CloseButton,
  Flex,
  Heading,
  IconAddLine,
  IconButton,
  IconRefreshLine,
  IconTableSplitCellsLine,
  List,
  Modal,
  Pill,
  Spinner,
  Tabs,
  Text,
  Tooltip,
} from "@instructure/ui";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import MappedIcon from "../CustomIcons/Mapped";
import MappedAssessmentIcon from "../CustomIcons/MappedAssessment";
import MappedCurrentAssessmentIcon from "../CustomIcons/MappedCurrentAssessment";
import CurrentMapNav from "../components/CurriculumMap/CurrentMapNav";
import CurriculumMapTermSelect from "../components/CurriculumMap/CurriculumMapTermSelect";
import ManageMap from "../components/CurriculumMap/ManageMap";
import Mapping from "../components/CurriculumMap/Mapping/Mapping";
import {
  ERROR,
  useNotification,
} from "../components/CustomHooks/NotificationProvider";
import { DialogYesNo } from "../components/DialogYesNo/DialogYesNo";
import { LearningOutcomesTypeSelect } from "../components/LearningOutcomes/LearningOutcomesTypeSelect";
import { sortOutcomes } from "../components/LearningOutcomes/utils";
import AcademicHierarchyMultiSelect from "../components/Selectors/AcademicHierarchySelect/HierarchyMulti";
import { CourseGroupSelect } from "../components/Selectors/CourseGroupSelect";
import { ProgramSelect } from "../components/Selectors/ProgramSelect";
import { MULTI, SINGLE, Selector } from "../components/Selectors/Selector";
import VarietySelector from "../components/Selectors/Variety/VarietySelector";
import { getEarliestCanvasTerm } from "../components/TermManagement/utils";
import { AttainmentLevelService } from "../services/AttainmentLevelService";
import { AttainmentRuleService } from "../services/AttainmentRuleService";
import { CurriculumMapService } from "../services/CurriculumMapService";
import { OfferingService } from "../services/OfferingService";
import { OutcomeCategoryService } from "../services/OutcomeCategoryService";
import {
  FeatureContext,
  TermsContext,
  UserDetailContext,
} from "../utils/Context";
import { isBetaOrDev } from "../utils/URIUtils";
import { sort } from "../utils/utils";

const IssuersContext = createContext();

const APPROVED = "approved";
const IN_PROGRESS = "in progress";
const FINALIZED = "finalized";

const CurriculumMap = ({ selectedAssessmentPlan, isDashboard }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { insightsTerms } = useContext(TermsContext);
  const userDetails = useContext(UserDetailContext);
  const features = useContext(FeatureContext);
  const [allLearningOutcomes, setAllLearningOutcomes] = useState(null);
  const [filteredLearningOutcomes, setFilteredLearningOutcomes] = useState([]);
  const [attainments, setAttainments] = useState(null);

  const [allCslosByCourse, setAllCslosByCourse] = useState(null);
  const [filteredCslosByCg, setFilteredCslosByCg] = useState([]);
  const [filteredCslosByCourse, setFilteredCslosByCourse] = useState([]);

  const [courseOptions, setCourseOptions] = useState([]);

  const [clearTrigger, setClearTrigger] = useState(false);
  const [clearCategories, setClearCategories] = useState(false);
  const [clearProgram, setClearProgram] = useState(false);
  const [rowTrigger, setRowTrigger] = useState(false);

  const [selectedHierarchies, setSelectedHierarchies] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const memoizedSelectedProgram = useMemo(
    () => (selectedProgram ? [selectedProgram] : []),
    [selectedProgram]
  );
  const [learningOutcomeType, setLearningOutcomeType] = useState("");
  const [variety, setVariety] = useState("outcome");
  const [selectedCourseGroups, setSelectedCourseGroups] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const [issuers, setIssuers] = useState([]);
  const [badgeClasses, setBadgeClasses] = useState([]);
  const [badgeMappings, setBadgeMappings] = useState([]);
  const [badging, setBadging] = useState(false);

  const [categories, setCategories] = useState([]);

  const [selectedCurriculumMap, setSelectedCurriculumMap] = useState("current");
  const [futureMap, setFutureMap] = useState(null);
  const [selectedFutureTerm, setSelectedFutureTerm] = useState(null);
  const [stateChanging, setStateChanging] = useState(false);
  const [creatingFutureMap, setCreatingFutureMap] = useState(false);
  const [futureMapFetchError, setFutureMapFetchError] = useState(false);

  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [mapModal, setMapModal] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);

  const ISLO = "islo";
  const PSLO = "pslo";

  useEffect(() => {
    if (features.find((f) => f.name === "badging_flag")?.value === "TRUE") {
      setBadging(true);
    }
  }, [features]);

  useEffect(() => {
    if (badging) {
      AttainmentRuleService.getIssuers()
        .then(async (issuers) => {
          issuers.forEach((issuer) => {
            issuer.id = issuer.entityId;
            issuer.label = issuer.name;
          });
          setIssuers(issuers);
          try {
            const badgeClassesArrays = await Promise.all(
              issuers.map((issuer) =>
                AttainmentRuleService.getBadgeClassesByIssuer(issuer.id)
              )
            );
            const allBadgeClasses = badgeClassesArrays.flat();
            setBadgeClasses(allBadgeClasses);
          } catch {
            showNotification(
              t("notifications.general.fetchError", {
                obj: t("badging.badgeClasses"),
              }),
              ERROR
            );
          }
        })
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("badging.issuers"),
            }),
            ERROR
          )
        );
      AttainmentRuleService.getBadgeMappings()
        .then((mappings) => {
          setBadgeMappings(mappings);
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("badging.badgeMappings"),
            }),
            ERROR
          );
        });
    }
  }, [badging, showNotification]);

  useEffect(() => {
    AttainmentLevelService.getAll().then((attainmentLevelList) => {
      attainmentLevelList.sort((o1, o2) => o1.seq - o2.seq);
      setAttainments(
        attainmentLevelList.map((degree) => ({
          id: degree.id,
          label: degree.name,
        }))
      );
    });
  }, []);

  useEffect(() => {
    if (selectedProgram) {
      setAllCslosByCourse(null);
      OfferingService.getOfferingsWithPlanDataForContext({
        contextId: selectedProgram,
        futureMappings: selectedCurriculumMap === "future",
      }).then((cslosByCourseWithPaging) => {
        const cslosByCourse = cslosByCourseWithPaging;
        filterCourseOptions(cslosByCourse);

        cslosByCourse.forEach((masterCourse, index) => {
          if (masterCourse.learning_outcomes.length !== 0) {
            cslosByCourse[index].learning_outcomes = sortOutcomes(
              masterCourse.learning_outcomes
            );
          }
        });

        setAllCslosByCourse(cslosByCourse);
        setFilteredCslosByCourse(cslosByCourse);
      });
    }
  }, [selectedProgram]);

  const fetchMappings = async (futureMappings) => {
    try {
      const [mappings, cslosByCourseWithPaging] = await Promise.all([
        AttainmentRuleService.getBadgeMappings(),
        OfferingService.getOfferingsWithPlanDataForContext({
          contextId: selectedProgram,
          futureMappings: futureMappings,
        }),
      ]);

      setBadgeMappings(mappings);

      const cslosByCourse = cslosByCourseWithPaging;
      if (selectedCourses.length > 0) {
        handleSelectedCourseOptions(
          selectedCourses,
          cslosByCourse,
          cslosByCourse
        );
      } else if (selectedCourseGroups.length > 0) {
        handleCourseGroupSelect(selectedCourseGroups, cslosByCourse);
      } else {
        filterCourseOptions(cslosByCourse);

        setAllCslosByCourse(cslosByCourse);
        setFilteredCslosByCourse(cslosByCourse);

        setRowTrigger(true);
      }
    } catch {
      showNotification(t("notifications.curriculumMap.fetchError"), ERROR);
    }
  };

  useEffect(() => {
    const controller1 = new AbortController();
    const signal1 = controller1.signal;
    const controller2 = new AbortController();
    const signal2 = controller2.signal;

    const fetchOutcomes = async () => {
      try {
        const context = learningOutcomeType === ISLO ? "ISLO" : selectedProgram;
        const promiseList = [
          OutcomeCategoryService.getOutcomeCategoriesByContext(
            context,
            null,
            signal1
          ),
          OutcomeCategoryService.getUncategorizedOutcomesByContext(
            context,
            signal2
          ),
        ];

        const [categories, uncategorizedOutcomes] =
          await Promise.all(promiseList);

        const filteredCategories = categories.filter(
          (cat) => cat.outcomes?.length > 0
        );
        const cats = filteredCategories.map((cat) => ({
          id: cat.ddl_id ?? cat.id,
          label: cat.name,
          outcomes: cat.outcomes,
        }));
        setCategories(cats);

        let outcomeList =
          context === "ISLO"
            ? uncategorizedOutcomes.institutional_outcomes
            : [
                ...uncategorizedOutcomes.level_outcomes,
                ...uncategorizedOutcomes.program_outcomes,
              ];
        outcomeList = outcomeList.filter((lo) => !lo.unapproved);
        cats.forEach((cat) => {
          cat.outcomes.forEach((outcome) => {
            outcomeList.push(outcome);
          });
        });
        const sortedOutcomes = sortOutcomes(outcomeList);
        setAllLearningOutcomes(sortedOutcomes);
        setFilteredLearningOutcomes(
          sortedOutcomes.filter((o) => o.outcome_variety === variety)
        );
        setRowTrigger(true);
      } catch (e) {
        if (e.name !== "AbortError") {
          showNotification(
            t("notifications.general.fetchError", { obj: t("dashboard.los") }),
            ERROR
          );
        }
      }
    };
    if (learningOutcomeType) {
      fetchOutcomes();
    }
    return () => {
      controller1.abort();
      controller2.abort();
    };
  }, [learningOutcomeType, selectedProgram, showNotification]);

  const filterCategories = () => {
    return categories.filter((c) =>
      c.outcomes.some((o) => o.outcome_variety === variety)
    );
  };

  const fetchCurriculumMaps = (programId) => {
    setFutureMapFetchError(false);
    CurriculumMapService.getCurriculumMapsByContextId(programId)
      .then((maps) => setFutureMap(maps.future_map))
      .catch(() => {
        setFutureMapFetchError(true);
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("manageMap.futureMap"),
          }),
          ERROR
        );
      });
  };

  const handleHierarchySelect = (hierarchies) => {
    setClearProgram(true);
    setSelectedProgram("");
    setLearningOutcomeType("");
    setRows([]);
    setCols([]);
    setSelectedHierarchies(hierarchies);
  };

  const handleProgramSelect = (programId) => {
    setFutureMap(null);
    setFutureMapFetchError(false);
    fetchCurriculumMaps(programId);
    setSelectedCurriculumMap("current");
    setClearProgram(false);
    setSelectedProgram(programId);
    setLearningOutcomeType("");
    setSelectedCourseGroups([]);
    setCourseOptions([]);
    setSelectedCourses([]);
    setRows([]);
    setCols([]);
    setClearTrigger(true);
  };

  const onLearningTypeSelect = (learningType) => {
    setClearTrigger(false);
    setClearCategories(true);
    setLearningOutcomeType(learningType);
  };

  const handleVarietySelect = (variety) => {
    setVariety(variety);
    setFilteredLearningOutcomes([
      ...allLearningOutcomes.filter((lo) => lo.outcome_variety === variety),
    ]);
    setRowTrigger(true);
  };

  const handleCategorySelect = (categoryIds) => {
    setClearCategories(false);
    if (categoryIds.length === 0) {
      setFilteredLearningOutcomes(
        allLearningOutcomes.filter((o) => o.outcome_variety === variety)
      );
    } else {
      const selectedCategories = categories.filter((c) =>
        categoryIds.includes(c.id)
      );
      setFilteredLearningOutcomes(
        selectedCategories
          .map((c) => c.outcomes)
          .flat()
          .filter((o) => o.outcome_variety === variety && !o.unapproved)
      );
    }
  };

  const handleCourseGroupSelect = (courseGroups, allCslos) => {
    setSelectedCourseGroups(courseGroups);
    setSelectedCourses([]);
    setClearTrigger(false);
    if (!!courseGroups && courseGroups.length > 0) {
      let selectedCourses = [];
      courseGroups.forEach((cg) =>
        cg.courses.forEach((course) => {
          if (
            !selectedCourses.find(
              (c) => course.id === c.course_summary.parent_course_id
            )
          ) {
            const matches = allCslos.filter(
              (c) => course.id === c.course_summary.parent_course_id
            );
            matches.forEach((c) => selectedCourses.push(c));
          }
        })
      );

      selectedCourses = sort(selectedCourses, (c) => c.code);
      filterCourseOptions(selectedCourses);
      setFilteredCslosByCg(selectedCourses);
      setFilteredCslosByCourse(selectedCourses);
    } else {
      filterCourseOptions(allCslos);
      setFilteredCslosByCourse(allCslos);
    }
    setRowTrigger(true);
  };

  const handleSelectedCourseOptions = (selections, allCslos, filteredCslos) => {
    setSelectedCourses(selections);
    if (!!selections && selections.length > 0) {
      const selectedCourses = allCslos.filter((c) => selections.includes(c.id));
      setFilteredCslosByCourse(selectedCourses);
    } else {
      if (selectedCourseGroups.length > 0) {
        setFilteredCslosByCourse(filteredCslos);
      } else {
        setFilteredCslosByCourse(allCslos);
      }
    }
    setRowTrigger(true);
  };

  const filterCourseOptions = (cslos) => {
    const optionList = [];

    cslos.forEach((course) => {
      const courseOption = {
        id: course.id,
        label: course.code,
        academicSubject: course.course_summary.academic_subject.name,
        level: course.level ? course.level.id : undefined,
      };
      optionList.push(courseOption);
    });
    optionList.sort((a, b) => {
      const textA = a.label.toUpperCase();
      const textB = b.label.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
    setCourseOptions(optionList);
  };

  const NoOutcomesAlert = () => {
    return (
      <Alert variant="info">
        {learningOutcomeType === PSLO
          ? t("curriculumMap.noPlos")
          : t("curriculumMap.noIlos")}
      </Alert>
    );
  };

  const handleCreateFutureMap = () => {
    const earliestCanvasTerm = getEarliestCanvasTerm(selectedFutureTerm);
    const futureMap = {
      authors: [{ id: userDetails.ddl_id, state: IN_PROGRESS }],
      current: false,
      term: { ddl_id: earliestCanvasTerm.id },
    };
    setCreatingFutureMap(true);
    CurriculumMapService.upsertCurriculumMap(futureMap, selectedProgram)
      .then((map) => {
        setFutureMap(map);
        fetchMappings(true);
      })
      .catch((e) => {
        e.json().then((res) => {
          if (res.message.includes("already has a future curriculum map")) {
            showNotification(t("manageMap.futureMapExists"), ERROR);
          } else {
            showNotification(
              t("notifications.general.createError", {
                obj: t("manageMap.futureMap"),
              }),
              ERROR
            );
          }
        });
      })
      .finally(() => setCreatingFutureMap(false));
  };

  const handleCheckInAndOut = (map, state) => {
    if (!map) {
      map = { current: selectedCurriculumMap === "current", authors: [] };
    }
    const body = {
      ...map,
      authors: [
        ...map.authors.filter((author) => author.id !== userDetails.ddl_id),
        { id: userDetails.ddl_id, name: userDetails.name, state: state },
      ],
    };
    setStateChanging(true);
    CurriculumMapService.upsertCurriculumMap(body, selectedProgram)
      .then((map) => {
        if (checkActiveTerm(map.term)) {
          fetchMappings(false);
          setSelectedCurriculumMap("current");
          setFutureMap(null);
        } else {
          setFutureMap(map);
        }
      })
      .finally(() => {
        setStateChanging(false);
      });
  };

  const getCheckedOutAuthor = (map) => {
    let author = null;
    if (!!map?.authors && map.authors.length !== 0) {
      author = map.authors.find((author) => author.state === IN_PROGRESS);
    }
    return author;
  };

  const getFinalizedAuthor = (map) => {
    let author = null;
    if (!!map?.authors && map.authors.length !== 0) {
      author = map.authors.find((author) => author.state === FINALIZED);
    }
    setIsFinalized(!!author);
    return author;
  };

  const editPermissions = (map) => {
    let permission = false;
    if (!!map?.authors && map.authors.length !== 0) {
      const author = map.authors.find(
        (author) => author.id === userDetails.ddl_id
      );
      if (author?.state === IN_PROGRESS) {
        permission = true;
      }
    }
    return permission;
  };

  const flipMapState = (in_progress) => {
    return in_progress ? APPROVED : IN_PROGRESS;
  };

  const fetchMappingData = (rows, cols) => {
    setRows(rows);
    setCols(cols);
  };

  const findInsightsTermFromCanvasTerm = (canvasTerm) => {
    return insightsTerms.find(
      (term) => term.id === canvasTerm.insights_term_id
    );
  };

  const checkActiveTerm = (canvasTerm) => {
    const insightsTerm = findInsightsTermFromCanvasTerm(canvasTerm);
    if (
      new Date(insightsTerm.start_at) <= new Date() &&
      new Date(insightsTerm.end_at) >= new Date()
    ) {
      return true;
    }
    return false;
  };

  const MapStateNav = ({ map }) => {
    const [loadingMap, setLoadingMap] = useState(false);
    const [currentTermConfirm, setCurrentTermConfirm] = useState(false);
    const author = getCheckedOutAuthor(map);
    const finalizedAuthor = getFinalizedAuthor(map);
    const [checkingMapIn, setCheckingMapIn] = useState(false);
    const notCheckedout = !author && !finalizedAuthor;

    return (
      <Flex margin="0 0 small 0" gap="small">
        <Flex.Item>
          <Pill
            statusLabel={t("status.status")}
            color={author ? "info" : "success"}
          >
            {finalizedAuthor
              ? t("curriculumMap.mapFinalized")
              : author
                ? t("curriculumMap.checkedOut")
                : t("curriculumMap.checkedIn")}
          </Pill>
        </Flex.Item>
        {(author || finalizedAuthor || notCheckedout) && (
          <Flex.Item shouldGrow>
            {author?.id === userDetails.ddl_id || notCheckedout ? (
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  if (checkActiveTerm(map.term)) {
                    setCurrentTermConfirm(true);
                  } else if (author) {
                    setCheckingMapIn(true);
                  } else {
                    handleCheckInAndOut(map, flipMapState(!!author));
                  }
                }}
                disabled={stateChanging || rows.length === 0 || !map.term}
                renderIcon={
                  stateChanging && (
                    <Spinner
                      size="x-small"
                      renderTitle={
                        author
                          ? t("curriculumMap.checkingOut")
                          : t("curriculumMap.checkingIn")
                      }
                    />
                  )
                }
              >
                {author
                  ? t("curriculumMap.checkIn")
                  : t("curriculumMap.checkOut")}
              </Button>
            ) : (
              <Text>
                <Text weight="bold">
                  {author
                    ? t("curriculumMap.checkedOutBy")
                    : t("curriculumMap.finalizedBy")}
                </Text>
                {author ? author.name : finalizedAuthor.name}
              </Text>
            )}
          </Flex.Item>
        )}
        <Flex.Item>
          <Button
            size="small"
            color="primary"
            renderIcon={IconTableSplitCellsLine}
            onClick={() => setMapModal(true)}
            disabled={rows.length === 0}
          >
            {t("curriculumMap.manageMap")}
          </Button>
        </Flex.Item>
        <Flex.Item>
          <Tooltip
            renderTip={t("manageMap.refreshMap")}
            color="primary-inverse"
          >
            <IconButton
              size="small"
              screenReaderLabel={t("manageMap.refreshMap")}
              color="primary"
              disabled={rows.length === 0 || !map.term || loadingMap}
              renderIcon={
                loadingMap ? (
                  <Spinner
                    renderTitle={t("global.pleaseWait")}
                    size="x-small"
                  />
                ) : (
                  <IconRefreshLine />
                )
              }
              onClick={async () => {
                setLoadingMap(true);
                await fetchMappings(selectedCurriculumMap === "future");
                setLoadingMap(false);
              }}
            />
          </Tooltip>
        </Flex.Item>
        <DialogYesNo
          message={t("manageMap.updateTermConfirm")}
          openStatus={currentTermConfirm}
          onClickNo={() => setCurrentTermConfirm(false)}
          onClickYes={() => {
            handleCheckInAndOut(map, flipMapState(!!author));
            setCurrentTermConfirm(false);
          }}
        />
        <Modal
          open={checkingMapIn}
          size="medium"
          label={t("curriculumMap.checkMapIn")}
          shouldCloseOnDocumentClick={false}
        >
          <Modal.Header>
            <Flex justifyItems="space-between">
              <Flex.Item>
                <Heading level="h2" as="h1">
                  {t("curriculumMap.checkMapIn")}
                </Heading>
              </Flex.Item>

              <Flex.Item>
                <CloseButton
                  offset="small"
                  onClick={() => {
                    setCheckingMapIn(false);
                  }}
                  screenReaderLabel={t("global.close")}
                />
              </Flex.Item>
            </Flex>
          </Modal.Header>
          <Modal.Body>
            <Flex wrap="wrap">
              <Flex.Item shouldGrow size="100%" margin="0 0 small 0">
                <Text as="p">{t("curriculumMap.checkInMapConfirm")}</Text>
              </Flex.Item>
              <Flex.Item>
                <Text as="p">{t("curriculumMap.finalizeMapConfirm")}</Text>
                <List isUnstyled>
                  <List.Item>
                    <Text color="secondary" wrap="break-word">
                      <em> ({t("curriculumMap.finalizeMapNote")})</em>
                    </Text>
                  </List.Item>
                </List>
              </Flex.Item>
            </Flex>
          </Modal.Body>
          <Modal.Footer>
            <Button
              margin="0 small 0 0"
              onClick={() => {
                handleCheckInAndOut(map, flipMapState(!!author));
                setCurrentTermConfirm(false);
              }}
              color="primary"
              disabled={stateChanging}
            >
              {stateChanging ? (
                <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
              ) : (
                t("curriculumMap.checkInMap")
              )}
            </Button>
            <Button
              onClick={() => {
                handleCheckInAndOut(map, FINALIZED);
                setCurrentTermConfirm(false);
                setIsFinalized(true);
              }}
              color="secondary"
              disabled={stateChanging}
            >
              {stateChanging ? (
                <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
              ) : (
                t("curriculumMap.finalize")
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Flex>
    );
  };

  return (
    <IssuersContext.Provider value={issuers}>
      {!isDashboard && (
        <Heading as="h1" level="h2" border="bottom" margin="0 0 small 0">
          {t("curriculumMap.headerTitle")}
        </Heading>
      )}
      <Flex gap="small">
        <Flex.Item shouldGrow>
          <Text size="small">{t("form.requiredLegend")}</Text>
        </Flex.Item>
        <Flex.Item>
          <Flex alignItems="center">
            <Flex.Item>
              <MappedIcon size="20px" margin="0 5px 0 0" />
            </Flex.Item>
            <Flex.Item>
              <Text size="small" lineHeight="fit">
                {t("curriculumMap.iconMappedLabel")}
              </Text>
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item>
          <Flex alignItems="center">
            <Flex.Item>
              <MappedAssessmentIcon size="20px" margin="0 5px 0 0" />
            </Flex.Item>
            <Flex.Item>
              <Text size="small" lineHeight="fit">
                {t("dashboard.associatedToAssessment")}
              </Text>
            </Flex.Item>
          </Flex>
        </Flex.Item>
        {isDashboard && (
          <Flex.Item>
            <Flex alignItems="center">
              <Flex.Item>
                <MappedCurrentAssessmentIcon size="20px" margin="0 5px 0 0" />
              </Flex.Item>
              <Flex.Item>
                <Text size="small" lineHeight="fit">
                  {t("curriculumMap.iconMappedCurrentAssessmentLabel")}
                </Text>
              </Flex.Item>
            </Flex>
          </Flex.Item>
        )}
      </Flex>
      <Flex
        justifyItems="space-between"
        alignItems="center"
        wrap="wrap"
        gap="small"
        margin="small 0 0 0"
      >
        <Flex.Item>
          <AcademicHierarchyMultiSelect selectHandler={handleHierarchySelect} />
        </Flex.Item>
        <Flex.Item>
          <ProgramSelect
            type={SINGLE}
            selectHandler={handleProgramSelect}
            selectedHierarchies={selectedHierarchies}
            size="small"
            clear={clearProgram}
            width="300px"
            isRequired={true}
          />
        </Flex.Item>
        <Flex.Item>
          <LearningOutcomesTypeSelect
            selectHandler={onLearningTypeSelect}
            program={selectedProgram}
            selectedOutcomeId={learningOutcomeType}
            trigger={clearTrigger}
            size="small"
            width="300px"
          />
        </Flex.Item>
      </Flex>
      <Flex
        justifyItems="space-between"
        alignItems="center"
        wrap="wrap"
        gap="medium"
        margin="small 0 medium 0"
      >
        <Flex.Item shouldGrow>
          <VarietySelector
            selectedItemId={variety}
            selectHandler={(v) => handleVarietySelect(v)}
          />
        </Flex.Item>
        <Flex.Item shouldGrow>
          <Selector
            size="small"
            type={MULTI}
            id="category-select"
            label={t("curriculumMap.category")}
            options={filterCategories()}
            selectHandler={handleCategorySelect}
            assistiveText={t("curriculumMap.selectCategories")}
            interaction={!!allLearningOutcomes}
            clear={clearCategories}
          />
        </Flex.Item>
        <Flex.Item shouldGrow>
          <CourseGroupSelect
            programIds={memoizedSelectedProgram}
            selectHandler={(cgs) =>
              handleCourseGroupSelect(cgs, allCslosByCourse)
            }
            clear={clearTrigger}
            size="small"
            interaction={!!allLearningOutcomes}
          />
        </Flex.Item>
        <Flex.Item shouldGrow>
          <Selector
            type={MULTI}
            id="curriculum-map-course-filter"
            label={t("curriculumMap.courseFilterLabel")}
            options={courseOptions}
            selectHandler={(courses) =>
              handleSelectedCourseOptions(
                courses,
                allCslosByCourse,
                filteredCslosByCg
              )
            }
            assistiveText={t("curriculumMap.selectCoursesAssistive")}
            interaction={!!allLearningOutcomes}
            size="small"
            selectedOptions={selectedCourses}
          />
        </Flex.Item>
        <Flex.Item size="100%">
          <Checkbox
            variant="toggle"
            label={t("learningOutcomes.archive.showArchived")}
            checked={showArchived}
            onChange={(e) => {
              setShowArchived(e.target.checked);
              setRowTrigger(true);
            }}
          />
        </Flex.Item>
      </Flex>
      {!selectedProgram || !learningOutcomeType ? (
        <div
          style={{
            textAlign: "center",
            border: "1px solid #ccc",
            padding: 10,
            borderRadius: "5px",
          }}
        >
          <Heading as="h3" level="h4">
            {t("curriculumMap.selectFollowing")}
          </Heading>
          <List isUnstyled>
            {!selectedProgram && <List.Item>{t("programs.program")}</List.Item>}
            {!learningOutcomeType && (
              <List.Item>{t("learningOutcomes.typeSelector")}</List.Item>
            )}
          </List>
        </div>
      ) : !allLearningOutcomes || !allCslosByCourse || !attainments ? (
        <Skeleton
          count={4}
          style={{ height: 120, borderRadius: 5, marginBottom: 5 }}
        />
      ) : allCslosByCourse.length === 0 ? (
        <Alert variant="info">{t("curriculumMap.noCourses")}</Alert>
      ) : filteredLearningOutcomes.length === 0 ? (
        <NoOutcomesAlert />
      ) : isDashboard ? (
        <Mapping
          key="dashboard-map"
          coursesWithOutcomes={filteredCslosByCourse}
          filteredLOs={filteredLearningOutcomes}
          attainments={attainments}
          setRowTrigger={setRowTrigger}
          rowTrigger={rowTrigger}
          learningOutcomeType={learningOutcomeType}
          dash={isDashboard}
          editable={false}
          selectedAssessmentPlan={selectedAssessmentPlan}
          badgeMappings={badgeMappings}
          setBadgeMappings={setBadgeMappings}
          showArchived={showArchived}
        />
      ) : (
        <Tabs
          onRequestTabChange={(_e, { index }) => {
            setSelectedCurriculumMap(index === 0 ? "current" : "future");
            if (index === 0 || (!!futureMap && index === 1)) {
              setFilteredCslosByCourse([]);
              fetchMappings(index === 1);
            }
            setSelectedFutureTerm(null);
          }}
        >
          <Tabs.Panel
            id="current"
            renderTitle={t("curriculumMap.current")}
            isSelected={selectedCurriculumMap === "current"}
          >
            <>
              <CurrentMapNav
                rows={rows}
                cols={cols}
                futureMap={futureMap}
                badgeMappings={badgeMappings}
              />
              <Mapping
                key="current-map"
                coursesWithOutcomes={filteredCslosByCourse}
                filteredLOs={filteredLearningOutcomes}
                attainments={attainments}
                setRowTrigger={setRowTrigger}
                rowTrigger={rowTrigger}
                learningOutcomeType={learningOutcomeType}
                dash={false}
                editable={false}
                selectedAssessmentPlan={selectedAssessmentPlan}
                badgeMappings={badgeMappings}
                setBadgeMappings={setBadgeMappings}
                date={new Date()}
                fetchMappingData={fetchMappingData}
                showArchived={showArchived}
              />
            </>
          </Tabs.Panel>
          <Tabs.Panel
            id="future"
            renderTitle={
              futureMap?.term
                ? `${t("curriculumMap.future")} (${
                    findInsightsTermFromCanvasTerm(futureMap.term).name
                  })`
                : t("curriculumMap.future")
            }
            isSelected={selectedCurriculumMap === "future"}
          >
            {futureMap ? (
              <>
                <MapStateNav map={futureMap} />
                {futureMap.term ? (
                  <Mapping
                    key="future-map"
                    coursesWithOutcomes={filteredCslosByCourse}
                    filteredLOs={filteredLearningOutcomes}
                    attainments={attainments}
                    setRowTrigger={setRowTrigger}
                    rowTrigger={rowTrigger}
                    learningOutcomeType={learningOutcomeType}
                    dash={false}
                    editable={editPermissions(futureMap)}
                    selectedAssessmentPlan={selectedAssessmentPlan}
                    badgeMappings={badgeMappings}
                    setBadgeMappings={setBadgeMappings}
                    date={new Date(futureMap?.term?.start_at)}
                    futureMap={futureMap}
                    fetchMappingData={fetchMappingData}
                    showArchived={showArchived}
                  />
                ) : (
                  <Alert variant="warning">
                    {t("manageMap.checkedOutWarning")}
                  </Alert>
                )}
              </>
            ) : futureMapFetchError ? (
              <Alert variant="error">
                {t("notifications.general.fetchError", {
                  obj: t("manageMap.futureMap"),
                })}
              </Alert>
            ) : (
              <Flex alignItems="end" gap="small">
                <Flex.Item>
                  <Text>{t("curriculumMap.createMap")}:</Text>
                </Flex.Item>
                <Flex.Item>
                  <CurriculumMapTermSelect
                    id="future-term-select"
                    label={t("curriculumMap.term")}
                    size="small"
                    selectHandler={(term) => setSelectedFutureTerm(term)}
                    selectedTermId={selectedFutureTerm?.id}
                    allowCurrent={isBetaOrDev(window.location.href)}
                  />
                </Flex.Item>
                <Flex.Item>
                  <Button
                    color="primary"
                    size="small"
                    renderIcon={
                      creatingFutureMap ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : (
                        IconAddLine
                      )
                    }
                    interaction={
                      selectedFutureTerm && !creatingFutureMap
                        ? "enabled"
                        : "disabled"
                    }
                    onClick={handleCreateFutureMap}
                  >
                    {t("academicHierarchy.create")}
                  </Button>
                </Flex.Item>
              </Flex>
            )}
          </Tabs.Panel>
        </Tabs>
      )}
      <ManageMap
        open={mapModal}
        setOpen={setMapModal}
        rows={rows}
        cols={cols}
        programId={selectedProgram}
        futureMap={futureMap}
        setFutureMap={setFutureMap}
        attainmentLevels={
          attainments ? attainments.map((a) => a.label.toLowerCase()) : []
        }
        permission={getCheckedOutAuthor(futureMap)?.id === userDetails.ddl_id}
        fetchMappings={fetchMappings}
        badgeMappings={badgeMappings}
        validIssuers={issuers}
        validBadgeClasses={badgeClasses}
        finalized={isFinalized}
      />
    </IssuersContext.Provider>
  );
};

export { CurriculumMap, IssuersContext };
